<script setup>
import { computed, inject, ref, watch } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { AvatarRoot, AvatarImage, AvatarFallback } from "radix-vue";
import Auth from "@/modules/Auth";
import debounce from "lodash/debounce";
import Notifications from "@/modules/Notifications";
import Watchlist from "@/modules/Watchlist";

const props = defineProps({
    product: {
        type: Object,
        required: true,
    },
    variant: {
        type: Object,
        required: true,
    },
});

defineEmits(["onProductClick"]);

const qty = ref(0);
const preQty = ref(0);
const localProduct = ref({ ...props.product });

const store = useStore();
const route = useRoute();
const axios = inject("axios");

const cart = computed(() => store.getters.cart);
const { isLoggedIn } = Auth();

watch(cart, mountCart, { immediate: true, deep: true });

watch(
    () => store.getters.getProduct,
    (newVal) => {
        if (newVal.data?.product?.id == props.product.id) {
            localProduct.value.watchlist = newVal.watchlist;
        }
    },
    { deep: true },
);

function mountCart() {
    if (isLoggedIn) {
        if (cart.value.itemsByCat?.length > 0) {
            cart.value.itemsByCat.forEach((element) => {
                element.items.forEach((item) => {
                    if (item.variantId == props.variant.id) {
                        qty.value = item.qty;
                        preQty.value = item.qty;
                    }
                });
            });

            if (cart.value.deleted_variant_id == props.variant.id) {
                qty.value = 0;
            }
        } else {
            qty.value = 0;
        }
    }
}

const updateCart = debounce((d) => {
    let [qty, add] = d;
    let data = {
        variant_id: props.variant.id,
        qty: qty,
        segmentQty: add ? qty - preQty.value : preQty.value - qty,
        axios: axios,
        route: route,
        add,
    };
    preQty.value = qty;
    if (qty === 0) {
        store.dispatch("removeFromCart", data);
    } else {
        store.dispatch("UpdateCart", data);
    }
}, 200);

const { notifyme, deleteNotify } = Notifications();

const notify = (variant) => {
    notifyme(variant, props.product);
    props.product.variants.forEach((item) => {
        if (item.id == variant.id) {
            item.has_notification = true;
        }
    });
};

const doNotNotify = (variant) => {
    deleteNotify(variant, props.product);
    props.product.variants.forEach((item) => {
        if (item.id == variant.id) {
            item.has_notification = null;
        }
    });
};

const lastOrdered = computed(() => {
    if (!props.variant.last_ordered) return "-";
    return moment(props.variant.last_ordered, "YYYYMMDD").fromNow();
});

const fallbackLabel = computed(() => {
    const name = props.variant?.opt1 || "";
    return name.trim() ? name.trim().slice(0, 2).toUpperCase() : "NA";
});

const stockStatus = computed(() => {
    if (props.variant?.available_stock && props.variant?.MaxOrderQty > 0) {
        return "In Stock";
    }

    if (props.variant?.first_landed_date == null) {
        return "Coming Soon";
    }

    return "Out of Stock";
});

const stockStatusClass = computed(() => {
    return stockStatus.value === "Out of Stock"
        ? "text-brand-red"
        : "text-brand-gray";
});

const canOrder = computed(() => {
    return props.variant.available_stock > 0
        && props.variant.banned == "false"
        && props.variant.price != null
        && props.variant.price != 0;
});

const maxOrderQty = computed(() => {
    return props.variant.MaxOrderQty > props.variant.available_stock
        ? props.variant.available_stock
        : props.variant.MaxOrderQty;
});

const { setWatchlist } = Watchlist();
const addtowatchlist = async () => {
    await setWatchlist(localProduct.value);
    localProduct.value.watchlist = localProduct.value.watchlist ? null : 1;
};
</script>

<template>
    <div class="mobile-product-row">
        <div class="mobile-product-header">
            <div class="product-main" @click="$emit('onProductClick', product.slug)">
                <AvatarRoot class="product-thumb">
                    <AvatarImage class="w-full h-full object-contain object-center" :src="variant.ImageThumbnailUrl"
                        :alt="variant.opt1" />
                    <AvatarFallback class="product-thumb-fallback">
                        {{ fallbackLabel }}
                    </AvatarFallback>
                </AvatarRoot>
                <div class="product-main-copy">
                    <p class="product-name">{{ product.name }}</p>
                    <span class="product-variant">{{ variant.opt1 }}</span>
                    <span class="product-stock" :class="stockStatusClass">{{ stockStatus }}</span>
                </div>
            </div>
            <button class="favorite-btn" @click.stop="addtowatchlist">
                <Icon class="!w-3 !h-3" :name="localProduct.watchlist ? 'heart-outline' : 'heart-filled'" />
                <span>{{ localProduct.watchlist ? "Remove Favorite" : "Add Favorite" }}</span>
            </button>
        </div>

        <ul class="mobile-product-meta">
            <li>
                <span>Price</span>
                <span>${{ Number(variant.price).toFixed(2) }}</span>
            </li>
            <li>
                <span>Wholesale Price</span>
                <span>${{ Number(variant.wholesale_price).toFixed(2) }}</span>
            </li>
            <li>
                <span>Last Ordered</span>
                <div class="meta-inline">
                    <span>{{ lastOrdered }}</span>
                    <span v-if="variant.isNew" class="new-badge">
                        New
                    </span>
                </div>
            </li>
            <li class="order-action-row">
                <span>Order</span>
                <div @click.stop>
                    <PlusMinusInput v-if="canOrder" class="medium" v-model:modelValue="qty" :tickIcon="true"
                        v-on:getQuantity="updateCart" :min="0" :max="maxOrderQty" />
                    <button v-else-if="variant.has_notification" :id="variant.id" @click="doNotNotify(variant)"
                        class="notify-bell" :class="{ undo: true }">
                        <span>Notification Set</span>
                        <div class="n-icon bg-black">
                            <Icon name="bell-off" class="w-4 h-4" />
                        </div>
                    </button>
                    <button v-else @click="notify(variant)" :id="variant.id" class="notify-bell">
                        <span>Notify Me</span>
                        <div class="n-icon text-brand-gray">
                            <Icon name="bell-ring" />
                        </div>
                    </button>
                </div>
            </li>
        </ul>
    </div>
</template>

<style lang="scss" scoped>
.mobile-product-row {
    @apply rounded-lg border border-gray-200 overflow-hidden bg-white;
}

.mobile-product-header {
    @apply px-4 py-4 bg-gray-50 border-b border-gray-100;
}

.product-main {
    @apply flex items-start gap-3 cursor-pointer;
}

.product-thumb {
    @apply w-12 h-12 border border-theme-border border-opacity-60 rounded bg-white flex-shrink-0 overflow-hidden;
}

.product-thumb-fallback {
    @apply w-full h-full flex items-center justify-center bg-gray-100 text-theme-body text-xxs font-medium uppercase;
}

.product-main-copy {
    @apply flex flex-col min-w-0;
}

.product-name {
    @apply text-sm font-medium text-theme-secondary leading-tight;
}

.product-variant {
    @apply mt-1 text-xs text-theme-light uppercase;
}

.product-stock {
    @apply mt-2 text-xxs uppercase;
}

.favorite-btn {
    @apply mt-3 inline-flex items-center gap-1 text-xs text-theme-light hover:text-theme-secondary;
}

.mobile-product-meta {
    @apply flex flex-col;

    li {
        @apply flex items-center justify-between gap-4 px-4 py-3 text-xs text-theme-body;

        &:nth-child(odd) {
            @apply bg-white;
        }

        &:nth-child(even) {
            @apply bg-gray-50;
        }

        >span:first-child {
            @apply font-medium text-theme-secondary;
        }
    }
}

.meta-inline {
    @apply flex items-center gap-2 justify-end text-right;
}

.new-badge {
    @apply inline-flex items-center justify-center rounded-full px-3 py-1 text-xxs;
    background: rgba(156, 228, 144, 0.31);
    color: #41b871;
}

.order-action-row {
    @apply items-start;

    >div {
        @apply flex justify-end;
        min-width: 150px;
    }
}

.notify-bell {
    @apply flex items-center space-x-2 uppercase text-xxs tracking-tight justify-end;

    .n-icon {
        @apply inline-flex items-center justify-center w-8 h-8 bg-brand-red text-white rounded;

        svg {
            @apply w-4 h-4;
        }
    }

    &.undo {
        .n-icon {
            @apply bg-theme-secondary;
        }
    }
}
</style>